// Gerçek ekran süresi: başlangıç (tıklama / sayfa geçişi / arama) → ekran kullanılabilir (veri geldi + ilk çizim).
// 15 sn aşılırsa "zaman aşımı" olarak kaydedilir; sekme arka plana alınırsa ayrı işaretlenir. Uygulamayı hiçbir koşulda bekletmez.
import { useEffect, useRef } from 'react';
import { busyNow, longNow, pushScreen } from './perfRum';

const TIMEOUT = 15000;
const pending = {};
let lastNav = null;
let navFrom = null;
let first = true;
try {
  document.addEventListener('click', () => { lastNav = performance.now(); navFrom = window.location.pathname; }, { capture: true, passive: true });
  window.addEventListener('popstate', () => { lastNav = performance.now(); navFrom = 'geri'; });
} catch { /* yoksay */ }

// Ekran açılmadan önce başlayan işlemler için (ör. giriş düğmesi)
export const markScreenStart = (action) => {
  try {
    pending[action] = performance.now();
    if (action === 'giris') sessionStorage.setItem('perf_giris_t', String(Date.now()));  // giriş sonrası sayfa yenilense de süre korunur
  } catch { /* yoksay */ }
};
try {
  const g = Number(sessionStorage.getItem('perf_giris_t'));
  if (g && Date.now() - g < 30000) pending.giris = performance.now() - (Date.now() - g);
} catch { /* yoksay */ }

// Dashboard girişten hemen sonra açılıyorsa 'giris' olarak ölçülür; izin listesine detay/sihirbaz/geri ile dönüş ayrı işlemdir
const resolve = (action) => {
  if (pending.giris != null && performance.now() - pending.giris < 30000) return 'giris';  // girişten sonra açılan ilk ölçülen ekran (rolüne göre Dashboard ya da izin listesi)
  if (action === 'izin_listesi_donus' && !(navFrom === 'geri' || /^\/work-permits\/.+/.test(navFrom || ''))) return null;
  return action;
};

let carry = null;  // bileşen hemen yeniden bağlanırsa (ör. yetki yenilemesi) ölçüm baştan başlamaz, sürer

const begin = (wanted) => {
  const now = performance.now();
  if (carry && carry.wanted === wanted && now - carry.at < 1500) {
    const c = carry; carry = null;
    return { ...c.m, done: false, busy0: c.m.busy0, hidden: c.m.hidden || document.visibilityState === 'hidden' };
  }
  const action = resolve(wanted);
  if (action === 'giris') { try { sessionStorage.removeItem('perf_giris_t'); } catch { /* yoksay */ } }
  const isFirst = first; first = false;
  const typed = action === 'personel_arama' ? pending[action] : null;
  const t0 = pending[action] ?? (lastNav != null && now - lastNav < 3000 ? lastNav : isFirst && now < TIMEOUT ? 0 : now);
  delete pending[action];
  return { action, t0, wait: typed != null ? now - typed : 0, busy0: busyNow(), long0: longNow(), hidden: document.visibilityState === 'hidden', done: false };
};

const end = (m, timedOut) => {
  const { action } = m;
  if (m.done || !action) return;
  m.done = true;
  const tReady = performance.now();
  const send = (render) => {
    const ms = timedOut ? TIMEOUT : performance.now() - m.t0;
    // Aşamalar örtüşmesin: sırayla ayrılır, toplam algılanan süreyi aşmaz
    let left = ms;
    const take = (v) => { const x = Math.max(0, Math.min(left, v)); left -= x; return x; };
    const ph = {};
    if (m.wait) ph.scr_input = take(m.wait);
    ph.scr_api = take(busyNow() - m.busy0);
    const l1 = longNow();
    if (l1 != null && m.long0 != null) ph.scr_long = take(l1 - m.long0);
    if (!timedOut) ph.scr_render = take(render);
    ph.scr_other = left;
    pushScreen({ a: m.hidden ? `${action}~arka` : action, ms, ph, tm: timedOut, bg: m.hidden });
  };
  if (timedOut) send(0);
  else requestAnimationFrame(() => setTimeout(() => send(performance.now() - tReady), 0));
};

// ready: ekran kullanılabilir olduğunda true; key değişince (ör. yeni arama) yeni ölçüm başlar
// action boşsa ölçüm yapılmaz. key ile yeniden başlayan ölçümde (ör. arama) önce 'hazır değil' görülmeli; eski sonuç 0 ms sayılmaz.
export const useScreenReady = (action, ready, key = 0) => {
  const m = useRef(null);
  const arms = useRef(0);
  const readyRef = useRef(ready);
  readyRef.current = ready;
  useEffect(() => {
    let timer, onVis;
    if (!action) return undefined;
    try {
      m.current = { ...begin(action), needEdge: arms.current++ > 0, sawFalse: !readyRef.current };
      const cur = m.current;
      onVis = () => { if (document.visibilityState === 'hidden') cur.hidden = true; };
      document.addEventListener('visibilitychange', onVis);
      timer = setTimeout(() => end(cur, true), TIMEOUT);
    } catch { /* yoksay */ }
    return () => {
      clearTimeout(timer); document.removeEventListener('visibilitychange', onVis);
      const cur = m.current;
      if (cur && !cur.done && cur.action) carry = { wanted: action, at: performance.now(), m: { ...cur } };
      if (cur) cur.done = true;
    };
  }, [action, key]);
  useEffect(() => {
    try {
      const cur = m.current;
      if (!action || !cur || cur.done) return;
      if (!ready) cur.sawFalse = true;
      else if (!cur.needEdge || cur.sawFalse) end(cur, false);
    } catch { /* yoksay */ }
  }, [action, ready, key]);
};
